"use client";

import { useLingui } from "@lingui/react/macro";
import { Skeleton } from "@offstreetparking/design-system/feedback";
import { OFFSTREET_COMPANY_LOGO, ThemeProvider } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import * as z from "zod/v4";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useCompany } from "~/components/providers/CompanyProvider";
import { offstreetClient } from "~/services/offstreetApi";
type CodeCheckPageProps = {
	children: React.ReactNode;
};
const queryParamsSchema = z.object({
	identifier: z.string().min(1).optional().catch(undefined),
});
export const CodeCheckPage = ({ children }: CodeCheckPageProps) => {
	const { t } = useLingui();
	const searchParams = useSearchParams();
	const identifier = queryParamsSchema.parse({
		identifier: searchParams.get("identifier"),
	}).identifier;
	const { company } = useCompany();
	const { data, isLoading } = offstreetClient.permitType.getBrandingForPermitTypes.useQuery(
		{
			identifier,
		},
		{
			enabled: !!identifier,
			retry: false,
		},
	);
	const companyLogo = useMemo(() => {
		if (company?.name && company?.branding?.companyLogoFileUrl) {
			return {
				url: company.branding.companyLogoFileUrl,
				altText: company.name,
			};
		}
		if (isLoading) {
			return undefined;
		}
		if (data?.branding?.companyLogoFile?.publicUrl) {
			return {
				url: data.branding.companyLogoFile.publicUrl,
				altText: data.whiteLabel?.portalName ?? "Offstreet",
			};
		}
		if (data?.whiteLabel?.portalLogo) {
			return {
				url: data.whiteLabel.portalLogo,
				altText: data.whiteLabel?.portalName ?? "Offstreet",
			};
		}
		return {
			url: OFFSTREET_COMPANY_LOGO,
			altText: "Offstreet",
		};
	}, [company, data, isLoading]);
	const branding = useMemo(() => {
		if (company?.branding) {
			return company.branding;
		}
		if (isLoading || !data?.branding) {
			return undefined;
		}
		return {
			id: 0,
			primaryColorRamp: data.branding.primaryColorRamp,
			accentColorRamp: data.branding.accentColorRamp,
		};
	}, [company, data, isLoading]);
	return (
		<CommonLayout
			srOnlyTitle={t`Code Check`}
			infoSlot={
				isLoading ? (
					<div className="flex flex-col gap-2">
						<Skeleton height={48} width={300} />
						<Skeleton height={48} width={500} />
						<Skeleton height={24} width={520} />
					</div>
				) : (
					<div className="space-y-8 pt-8">
						<Text weight="bolder" size="5xl">{t`Code Check`}</Text>
						<Text size="sm">{t`Look up a registration code to check its validity and usage.`}</Text>
					</div>
				)
			}
			companyLogo={companyLogo}
		>
			<ThemeProvider branding={branding} />
			<ContentCard>
				{isLoading && (
					<div className="flex flex-col gap-2 p-4">
						<Skeleton height={48} width={300} />
						<Skeleton height={48} width={500} />
						<Skeleton height={24} width={520} />
					</div>
				)}
				{!isLoading && children}
			</ContentCard>
		</CommonLayout>
	);
};
